import { Box, Tab, Tabs, Typography, CircularProgress } from '@mui/material';
import PropTypes from 'prop-types';
import './StudentWalletViewAll.css';
import React from 'react';
import { useState, useEffect, useCallback } from 'react';
import ReceivedIcon from '../../../../../../assets/Admin/EarningIcons/received-icon.svg';
import WithdrawalIcon from '../../../../../../assets/Admin/EarningIcons/withdrawal-icon.svg';
import DisputeIcon from '../../../../../../assets/Admin/EarningIcons/dispute-icon.svg';
import OneOnOneSessionIcon from '../../../../../../assets/Admin/SessionIcons/one-on-one-session-icon.svg';
import GroupSessionIcon from '../../../../../../assets/Admin/SessionIcons/group-session-icon.svg';
import PrimeSessionIcon from '../../../../../../assets/Admin/SessionIcons/prime-session-icon.svg';
import { axiosInstance } from '../../../../../../utils/tokenUtils';

function formatText(input) {
  return input
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/\b\w/g, char => char.toUpperCase()); // Capitalize first letter of each word
}

// Payment types that CREDIT the student's wallet (money in → shown with "+").
// Everything else is a debit ("-"). Mirrors the backend wallet-mutation logic:
// wallet_top_up / refunded / dispute add koins; session_booking / session_extension / penalties subtract.
const CREDIT_PAYMENT_TYPES = ['wallet_top_up', 'refunded', 'dispute'];

const WalletHistoryMap = {
  session_booking: {
    class: 'withdrawal-view-all-icon-container',
    icon: WithdrawalIcon,
    amountClass: 'single-view-all-wallet-history-session-booking-amount-text',
  },
  session_extension: {
    class: 'withdrawal-view-all-icon-container',
    icon: WithdrawalIcon,
    amountClass: 'single-view-all-wallet-history-session-booking-amount-text',
  },
  penalties: {
    class: 'dispute-view-all-icon-container',
    icon: DisputeIcon,
    amountClass: 'single-view-all-wallet-history-cancellation-amount-text',
  },
  wallet_top_up: {
    class: 'received-view-all-icon-container',
    icon: ReceivedIcon,
    amountClass: 'single-view-all-wallet-history-add-fund-amount-text',
  },
  refunded: {
    class: 'received-view-all-icon-container',
    icon: ReceivedIcon,
    amountClass: 'single-view-all-wallet-history-add-fund-amount-text',
  },
  dispute: {
    class: 'received-view-all-icon-container',
    icon: ReceivedIcon,
    amountClass: 'single-view-all-wallet-history-add-fund-amount-text',
  },
};

const SessionTypeColor = {
  one_on_one: {
    color: '#6B60E7',
    bgcolor: '#E8F0FF',
    value: 'One-On-One',
    icon: OneOnOneSessionIcon,
  },
  group: { color: '#279BE2', bgcolor: '#FAFFFB', value: 'Group', icon: GroupSessionIcon },
  prime: { color: '#AD8B00', bgcolor: '#F0EFFF', value: 'Prime', icon: PrimeSessionIcon },
};

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const StudentWalletViewAll = ({ studentId }) => {
  const [value, setValue] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const pageSize = 30;

  const getPaymentTypeFilter = tabIndex => {
    switch (tabIndex) {
      case 1: // Payments
        return ['session_booking', 'session_extension', 'penalties'];
      case 2: // Add Fund
        return ['wallet_top_up'];
      case 3: // Cancellations
        return ['penalties'];
      default: // All
        return null;
    }
  };

  const fetchTransactions = useCallback(
    async (pageNumber = 1, resetData = false) => {
      try {
        if (pageNumber === 1) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }

        const response = await axiosInstance.get(
          `/admin/students/${studentId}/transactions-history?page=${pageNumber}&page_size=${pageSize}`
        );

        const newTransactions = response.data.results || [];
        setTotalCount(response.data.count || 0);

        if (resetData || pageNumber === 1) {
          setTransactions(newTransactions);
        } else {
          setTransactions(prev => [...prev, ...newTransactions]);
        }

        setHasMore(
          newTransactions.length === pageSize &&
            response.data.offset + newTransactions.length < response.data.count
        );
      } catch (error) {
        console.error('Error fetching transactions:', error);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [studentId, pageSize]
  );

  useEffect(() => {
    if (studentId) {
      setPage(1);
      fetchTransactions(1, true);
    }
  }, [studentId, fetchTransactions]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const loadMore = () => {
    if (!loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchTransactions(nextPage, false);
    }
  };

  const formatDateTime = dateString => {
    const date = new Date(dateString);
    const options = {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };
    return date.toLocaleDateString('en-US', options);
  };

  const formatDateForGrouping = dateString => {
    const date = new Date(dateString);
    const options = {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    };
    return date.toLocaleDateString('en-US', options);
  };

  const getFilteredTransactions = () => {
    const paymentTypes = getPaymentTypeFilter(value);
    if (!paymentTypes) return transactions;

    return transactions.filter(transaction => paymentTypes.includes(transaction.payment_type));
  };

  const groupTransactionsByDate = transactions => {
    const grouped = {};

    transactions.forEach(transaction => {
      const dateKey = formatDateForGrouping(transaction.created_at);
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(transaction);
    });

    // Sort dates in descending order (newest first)
    const sortedDates = Object.keys(grouped).sort((a, b) => {
      return new Date(b) - new Date(a);
    });

    const result = {};
    sortedDates.forEach(date => {
      result[date] = grouped[date];
    });

    return result;
  };

  const getTransactionIcon = paymentType => {
    return WalletHistoryMap[paymentType] || WalletHistoryMap.session_booking;
  };

  const getAmountPrefix = paymentType => {
    return CREDIT_PAYMENT_TYPES.includes(paymentType) ? '+' : '-';
  };

  const renderTransactionItem = item => {
    const transactionIcon = getTransactionIcon(item.payment_type);

    return (
      <Box component="table" className="single-view-all-wallet-history-container" key={item?.id}>
        <tbody>
          <tr>
            <td style={{ width: '40%' }}>
              <Box className="flex items-center gap-4">
                <Box className={transactionIcon?.class}>
                  <img src={transactionIcon?.icon || '/placeholder.svg'} alt={item?.payment_type} />
                </Box>
                <Box className="flex flex-col">
                  <div className="single-view-all-wallet-history-title-text">
                    {formatText(item?.event_type_title)}
                  </div>
                  <div className="single-view-all-wallet-history-subtitle-text">
                    {formatText(item?.payment_type)}
                  </div>
                </Box>
              </Box>
            </td>
            <td style={{ width: '20%' }}>
              {item?.session_type ? (
                <Box className="flex items-center gap-2">
                  <img
                    src={SessionTypeColor[item?.session_type]?.icon || '/placeholder.svg'}
                    alt="Session type icon"
                  />
                  <Typography
                    className="session-type-text"
                    sx={{ color: SessionTypeColor[item?.session_type]?.color }}
                  >
                    {SessionTypeColor[item?.session_type]?.value}
                  </Typography>
                </Box>
              ) : (
                <Typography className="session-type-text" sx={{ color: '#949da6' }}>
                  ---
                </Typography>
              )}
            </td>
            <td style={{ width: '25%' }}>
              <div className="date-time-text">{formatDateTime(item?.created_at)}</div>
            </td>
            <td style={{ width: '15%', textAlign: 'right' }}>
              <div className={transactionIcon?.amountClass}>
                {getAmountPrefix(item?.payment_type)} {item?.koins} Koins
              </div>
            </td>
          </tr>
        </tbody>
      </Box>
    );
  };

  const renderTransactionGroup = groupedTransactions => {
    return Object.entries(groupedTransactions).map(([date, dayTransactions]) => (
      <Box key={date} className="flex flex-col gap-[12px]">
        <div className="font-medium text-gray-700">{date}</div>
        <Box className="flex flex-col gap-[12px]">{dayTransactions.map(renderTransactionItem)}</Box>
      </Box>
    ));
  };

  // No data message component
  const NoDataMessage = () => (
    <Box className="flex justify-center items-center py-8">
      <Typography variant="body1" color="text.secondary">
        No transaction history available
      </Typography>
    </Box>
  );

  const renderTabContent = () => {
    const filteredTransactions = getFilteredTransactions();
    const groupedTransactions = groupTransactionsByDate(filteredTransactions);
    const hasTransactions = Object.keys(groupedTransactions).length > 0;

    if (loading) {
      return (
        <Box className="flex justify-center items-center py-8">
          <CircularProgress />
        </Box>
      );
    }

    if (!hasTransactions) {
      return <NoDataMessage />;
    }

    return (
      <Box className="flex flex-col gap-[24px]">
        {renderTransactionGroup(groupedTransactions)}

        {/* Load More Button / Loading */}
        {hasMore && (
          <Box className="flex justify-center py-4">
            {loadingMore ? (
              <CircularProgress size={24} />
            ) : (
              <button
                onClick={loadMore}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
              >
                Load More
              </button>
            )}
          </Box>
        )}

        {/* End of data indicator */}
        {!hasMore && transactions.length > 0 && (
          <Box className="flex justify-center py-4">
            <Typography variant="body2" color="text.secondary">
              {totalCount} transaction{totalCount !== 1 ? 's' : ''} total
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box
      data-testid="student-wallet-component"
      className="h-[calc(100vh-260px)] overflow-auto relative bg-[#F9F9F9] rounded-lg"
    >
      <Box sx={{ width: '100%' }}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            background: '#FFF',
            margin: '15px 15px 0px 15px',
          }}
        >
          <Tabs value={value} onChange={handleChange} aria-label="wallet tabs">
            <Tab label="All" sx={{ textTransform: 'none' }} {...a11yProps(0)} />
            <Tab label="Payments" sx={{ textTransform: 'none' }} {...a11yProps(1)} />
            <Tab label="Add Fund" sx={{ textTransform: 'none' }} {...a11yProps(2)} />
            <Tab label="Cancellations" sx={{ textTransform: 'none' }} {...a11yProps(3)} />
          </Tabs>
        </Box>

        <CustomTabPanel value={value} index={0}>
          {renderTabContent()}
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          {renderTabContent()}
        </CustomTabPanel>
        <CustomTabPanel value={value} index={2}>
          {renderTabContent()}
        </CustomTabPanel>
        <CustomTabPanel value={value} index={3}>
          {renderTabContent()}
        </CustomTabPanel>
      </Box>
    </Box>
  );
};

export default StudentWalletViewAll;
